import type { ReactElement } from 'react';
import { Link } from 'react-router';
import type { EventBrief } from '@contracts/calendar';
import { useMe } from '../api/queries';
import { Card, Pill, Who } from '../ui';
import { dayLabel, since } from '../lib/time';

/**
 * What a brief actually says — open going in, last time, who is coming, what
 * we know about them.
 *
 * Exported because it belongs on the **meeting's** page too. Vladimir,
 * 2026-09-21: "I don't think we shall have two different pages for briefs and
 * notes, just show brief in the meetings page of the meetings is not started
 * yet". Since PRD 43 the meeting exists before it happens, so the brief is a
 * state of that page rather than a page of its own.
 */
export function BriefBody({ b }: { b: EventBrief }): ReactElement {
  const me = useMe();
  const myEmail = me.data?.email ?? '';
  return (
    <>
      <div className="grid-2 aside">
        <div className="stack-lg">
          <Card
            title="Open going in"
            aside={
              <span className="t-secondary">
                {b.open.length === 0
                  ? 'nothing outstanding'
                  : `${b.open.length} ${b.open.length === 1 ? 'commitment' : 'commitments'}`}
              </span>
            }
            flush
          >
            {b.open.length === 0 ? (
              <p className="t-secondary" style={{ padding: '14px 16px' }}>
                Nothing is owed going into this one.
              </p>
            ) : (
              <div className="rows">
                {b.open.map((i) => (
                  <Link key={i.id} to={`/c/${i.id}`} className="row">
                    <span className="row-main">
                      <span className="row-title">{i.text}</span>
                      <span className="row-sub">
                        {i.ownerEmail ? (
                          <Who
                            email={i.ownerEmail}
                            name={i.ownerName}
                            me={i.ownerEmail.toLowerCase() === myEmail.toLowerCase()}
                          />
                        ) : (
                          <span className="mono">nobody yet</span>
                        )}
                        <span className="mono">
                          {i.meeting.title} · {since(i.createdAt)}
                        </span>
                        <Pill>{i.grade.toLowerCase()}</Pill>
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </Card>

          <Card
            title="Last time"
            aside={
              b.lastTime ? (
                <Link to={`/m/${b.lastTime.meetingId}`} className="t-secondary">
                  {b.lastTime.title} · {dayLabel(b.lastTime.startedAt)} →
                </Link>
              ) : null
            }
          >
            {!b.lastTime ? (
              <p className="t-secondary">No earlier meeting of this kind that you can open.</p>
            ) : (
              <div className="stack" style={{ gap: 10 }}>
                {b.lastTime.summary.length ? (
                  b.lastTime.summary.map((p, i) => (
                    <p key={i} className="t-body" style={{ maxWidth: '65ch' }}>
                      {p}
                    </p>
                  ))
                ) : (
                  <p className="t-secondary">No note was written for it.</p>
                )}
                {b.lastTime.decisions.length ? (
                  <div className="stack" style={{ gap: 6 }}>
                    <span className="eyebrow">Decided then</span>
                    {b.lastTime.decisions.map((d) => (
                      <span key={d.id}>
                        {d.text}{' '}
                        <Pill tone={d.status === 'stands' ? 'green' : undefined}>{d.status}</Pill>
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            )}
          </Card>
        </div>

        <div className="stack-lg">
          <Card title="Who is coming" flush>
            {b.attendees.length === 0 ? (
              <p className="t-secondary" style={{ padding: '14px 16px' }}>
                Nobody else is on the invitation.
              </p>
            ) : (
              b.attendees.map((a) => (
                <div key={a.email} className="cite">
                  <span className="cluster" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <Who
                      email={a.email}
                      name={a.name}
                      to={`/people/${encodeURIComponent(a.email)}`}
                    />
                    {a.guest ? <Pill>{a.company ? `guest · ${a.company}` : 'guest'}</Pill> : null}
                  </span>
                  <span className="when">
                    {a.meetingsTogether === 0
                      ? 'never met'
                      : `met ${a.meetingsTogether === 1 ? 'once' : `${a.meetingsTogether} times`}${a.lastMetAt ? `, last ${since(a.lastMetAt)}` : ''}`}
                    {a.owesYou ? ` · owes you ${a.owesYou}` : ''}
                    {a.youOwe ? ` · you owe ${a.youOwe}` : ''}
                  </span>
                </div>
              ))
            )}
          </Card>

          <Card title="What we know about them" flush>
            {b.knowledge.length === 0 ? (
              <p className="t-secondary" style={{ padding: '14px 16px' }}>
                Nothing kept yet about these people, their companies or this series.
              </p>
            ) : (
              b.knowledge.map((e) => (
                <div key={e.id} className="cite">
                  <span>
                    <span className="kind">{e.kind}</span> {e.statement}
                  </span>
                  <span className="when">
                    {e.subject?.name ? `about ${e.subject.name} · ` : ''}
                    came up {e.sightings === 1 ? 'once' : `${e.sightings} times`}
                  </span>
                </div>
              ))
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
